import { useUnitOptions } from '@app/ccf/company-report/utils';
import {
  BulkAction,
  BulkModalBodyProps,
} from '@app/components/data-table/bulk-edit/types';
import {
  ChangeItemTypeOptions,
  CurrentItemType,
  ItemType,
  useChangeItemType,
} from '@app/components/excluded-items/use-change-item-type';
import { useOptimisticFoodResolution } from '@app/components/food-matching/bulk/use-optimistic-food-resolution';
import { NeedsReviewBadge } from '@app/components/item-edit/needs-review-badge';
import { PackagingTypePicker } from '@app/components/item-edit/packaging-type-picker';
import { useGetPackagingCCFDefaults } from '@shared/api/hooks/ccf/activities/packaging';
import { useGetActivityTypeUnits } from '@shared/api/hooks/ccf/units';
import { useBulkUpdatePurchasedGoods } from '@shared/api/hooks/purchased-goods/bulk';
import { useBulkUpdateSoldGoods } from '@shared/api/hooks/sold-goods/bulk';
import {
  CCFPackagingGroupCategoryEnum,
  IngredientPickerItem,
  OperationReportTypeEnum,
} from '@shared/api/types';
import Heading from '@shared/components/content/heading';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import IngredientPicker from '@shared/components/pickers/ingredient-picker';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import { useToast } from '@shared/components/ui/use-toast';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

type FoodModelPath = 'basic_food' | 'modelled_food';

const TYPE_OPTIONS: Record<
  ItemType,
  { icon: keyof typeof Icons; label: string; description: string }
> = {
  food: {
    icon: 'carrot',
    label: 'Food and drink',
    description:
      'Add to Food & drink and calculate emissions from its ingredients.',
  },
  packaging: {
    icon: 'package',
    label: 'Packaging',
    description: 'Add to Packaging and calculate emissions from its material.',
  },
  other: {
    icon: 'archive',
    label: 'Other goods',
    description: 'Moves to Other goods, which stay out of your footprint.',
  },
};

const TARGET_ORDER: ItemType[] = ['food', 'packaging', 'other'];

const FOOD_MODEL_OPTIONS: Record<
  FoodModelPath,
  { icon: keyof typeof Icons; label: string; description: string }
> = {
  basic_food: {
    icon: 'carrot',
    label: 'Basic food',
    description: 'Match every selected item to one food from our library.',
  },
  modelled_food: {
    icon: 'chefHat',
    label: 'Recipe',
    description: 'AI-generate a recipe per item from its name.',
  },
};

const FOOD_MODEL_ORDER: FoodModelPath[] = ['basic_food', 'modelled_food'];

/**
 * The row's own data, passed by single-item (per-row) edits so the modal can
 * show the original client data and prefill the current modelling and pack
 * weight. Absent in bulk.
 */
export interface UpdateItemRowDetails {
  name: string;
  externalId?: string | null;
  /** The original input unit string from the client's file, e.g. "each". */
  inputUnit?: string | null;
  itemsPerPack?: number | null;
  itemMassOrVolume?: number | null;
  /** The item unit's symbol, e.g. "kg" (the list rows carry symbols). */
  itemUnitSymbol?: string | null;
  /**
   * How the client's file quantified the item: 'packs' or 'total-weight'.
   * Total-weight items have no pack weight to configure.
   */
  inputMode?: string | null;
  /** The current basic-food match, when the item is already matched food. */
  baseFood?: { uuid: string; name: string } | null;
  /** The current packaging type, when the item is already packaging. */
  packaging?: { groupName: string; typeUuid: string } | null;
  /** Number of packs purchased and total spend, for the price-per-kg readout. */
  quantity?: number | null;
  totalSpend?: number | null;
  /** Review flags — which dimension the pipeline wants a human to check. */
  itemTypeNeedsReview?: boolean;
  matchNeedsReview?: boolean;
  weightNeedsReview?: boolean;
}

interface UpdateItemsModalBodyProps extends BulkModalBodyProps {
  /**
   * The type the selected items currently are. Non-food current types hide
   * their own option (a same-type change is a no-op); a food current type
   * keeps "Food" available — it re-models the existing food activities via
   * the model-as actions instead of change-type.
   */
  currentType: CurrentItemType;
  /** Row data for single-item edits; enables Original data + Pack weight. */
  itemDetails?: UpdateItemRowDetails;
  /** Purchased vs sold endpoints; defaults to purchase. */
  reportType?: OperationReportTypeEnum;
}

interface PackWeightFormValues {
  externalId: string;
  name: string;
  inputUnit: string;
  itemsPerPack: string;
  itemWeight: string;
  unit: string;
}

/** Parse a form text value into a float, or undefined when blank/invalid. */
function parseWeightField(value: string | undefined): number | undefined {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function UpdateItemsModalBody({
  selectedUuids,
  selectedFoodActivityUuids,
  selectedCount,
  onClose,
  onCompleted,
  currentType,
  itemDetails,
  reportType = OperationReportTypeEnum.purchase,
}: UpdateItemsModalBodyProps) {
  const { toast } = useToast();
  // The rich single-item view (original data, prefill, pack weight) is driven
  // by itemDetails, passed only by the per-row edit button. The bulk action
  // never passes it, so any checkbox selection — one row or many — opens the
  // lean bulk variant.
  const isSingleItem = !!itemDetails;

  // Prefill the item type for a single item so editing an already-classified
  // item opens on its current type: food re-models its existing activities,
  // packaging re-models in place with its current type/size. Bulk and
  // uncertain items open unselected.
  const prefillTarget: ItemType | null =
    isSingleItem && currentType === 'food' && selectedFoodActivityUuids?.length
      ? 'food'
      : isSingleItem && currentType === 'packaging' && itemDetails?.packaging
        ? 'packaging'
        : null;
  const [target, setTarget] = useState<ItemType | null>(prefillTarget);
  const [foodModel, setFoodModel] = useState<FoodModelPath | null>(
    isSingleItem && currentType === 'food' && itemDetails?.baseFood
      ? 'basic_food'
      : null
  );
  const [selectedBasicFood, setSelectedBasicFood] =
    useState<IngredientPickerItem | null>(
      itemDetails?.baseFood
        ? {
            uuid: itemDetails.baseFood.uuid,
            name: itemDetails.baseFood.name,
            type: 'base_food',
            availableUnitTypes: [],
          }
        : null
    );
  const [packagingGroupName, setPackagingGroupName] = useState<string>(
    itemDetails?.packaging?.groupName ?? ''
  );
  const [packagingTypeUuid, setPackagingTypeUuid] = useState<string>(
    itemDetails?.packaging?.typeUuid ?? ''
  );
  // Whether the user has addressed a flagged match (picked a food or model).
  const [matchEdited, setMatchEdited] = useState(false);

  // One form hosts the read-only original data, the pack-weight fields and
  // the pickers' form context; modelling selections live in local state.
  const form = useForm<PackWeightFormValues>({
    defaultValues: {
      externalId: itemDetails?.externalId ?? '',
      name: itemDetails?.name ?? '',
      inputUnit: itemDetails?.inputUnit ?? '',
      itemsPerPack: itemDetails?.itemsPerPack?.toString() ?? '',
      itemWeight: itemDetails?.itemMassOrVolume?.toString() ?? '',
      unit: '',
    },
  });

  const { changeType, isLoading: isChangingType } =
    useChangeItemType(reportType);
  // The model-as actions target the food activities of items that are already
  // food — the same operation as the former "Update matched food" modal.
  // Both hooks are instantiated unconditionally (rules of hooks); only the one
  // matching reportType is used.
  const purchasedFoodActivityBulk = useBulkUpdatePurchasedGoods();
  const soldFoodActivityBulk = useBulkUpdateSoldGoods();
  const foodActivityBulk =
    reportType === OperationReportTypeEnum.sales
      ? soldFoodActivityBulk
      : purchasedFoodActivityBulk;
  const resolveReviewRows = useOptimisticFoodResolution(reportType);

  const { data: packagingGroups, isLoading: isPackagingLoading } =
    useGetPackagingCCFDefaults();
  const { data: units, isLoading: isUnitsLoading } =
    useGetActivityTypeUnits('food');
  const unitOptions = useUnitOptions(units!);

  // The list rows carry the unit as a symbol; the API wants its uuid. Resolve
  // once the units land and seed the select.
  useEffect(() => {
    if (!units?.length) return;
    const matchingUnit = units.find(
      (unit) =>
        (itemDetails?.itemUnitSymbol &&
          unit.symbol === itemDetails.itemUnitSymbol) ||
        // Total-weight items carry no item unit; their unit is the input
        // unit from the file (stored as the unit name, e.g. "kilogram").
        (itemDetails?.inputUnit &&
          (unit.name === itemDetails.inputUnit ||
            unit.symbol === itemDetails.inputUnit))
    );
    if (matchingUnit) form.setValue('unit', matchingUnit.uuid);
  }, [units, itemDetails?.itemUnitSymbol, itemDetails?.inputUnit, form]);

  const isLoading = isChangingType || foodActivityBulk.isLoading;

  // A food current type keeps its own option only when the food activities
  // are resolvable (activity-keyed tables pass them alongside the PG uuids).
  const remodelsExistingFood =
    currentType === 'food' && !!selectedFoodActivityUuids?.length;
  // A packaging item edited on its own keeps the Packaging option so its
  // type / size / items-per-pack can be changed in place.
  const remodelsExistingPackaging =
    currentType === 'packaging' && isSingleItem && !!itemDetails?.packaging;
  const typeOptions = TARGET_ORDER.filter(
    (type) =>
      type !== currentType ||
      (type === 'food' && remodelsExistingFood) ||
      (type === 'packaging' && remodelsExistingPackaging)
  );

  const availablePackagingGroups = useMemo(
    () =>
      (packagingGroups ?? []).filter(
        (group) =>
          group.category !== CCFPackagingGroupCategoryEnum.non_packaging_items
      ),
    [packagingGroups]
  );
  const availableSizes = useMemo(() => {
    const selectedGroup = availablePackagingGroups.find(
      (group) => group.name === packagingGroupName
    );
    return (selectedGroup?.sizes ?? []).map((size) => ({
      label: `${size.size}${
        size.sizeDescription ? `, ${size.sizeDescription.toLowerCase()}` : ''
      }`,
      value: size.uuid,
    }));
  }, [availablePackagingGroups, packagingGroupName]);

  const handleTargetSelect = (value: string) => {
    setTarget(value as ItemType);
    setFoodModel(null);
    setSelectedBasicFood(null);
    setPackagingGroupName('');
    setPackagingTypeUuid('');
  };

  const handleFoodModelSelect = (value: string) => {
    setFoodModel(value as FoodModelPath);
    if (value !== 'basic_food') setSelectedBasicFood(null);
    setMatchEdited(true);
  };

  const handlePackagingGroupSelect = (value: string) => {
    setPackagingGroupName(value);
    // The size list is per-group, so a group change resets the size.
    setPackagingTypeUuid('');
  };

  // Total-weight items have no pack weight — their purchased weight comes
  // straight from the file's totals (mirrors the item page, which only shows
  // Pack weight for pack-configured items).
  const isTotalWeightInput = itemDetails?.inputMode === 'total-weight';
  const showPackWeight = isSingleItem && !!target && target !== 'other';

  // Editable pack weight is required before Apply: all three fields for
  // food, items per pack for packaging.
  const [watchedItemsPerPack, watchedItemWeight, watchedUnit] = form.watch([
    'itemsPerPack',
    'itemWeight',
    'unit',
  ]);
  const isPackWeightEditable = showPackWeight && !isTotalWeightInput;
  const packWeightComplete =
    !isPackWeightEditable ||
    (target === 'packaging'
      ? parseWeightField(watchedItemsPerPack) !== undefined
      : parseWeightField(watchedItemsPerPack) !== undefined &&
        parseWeightField(watchedItemWeight) !== undefined &&
        !!watchedUnit);

  // Pack weight is derived live from items per pack × item weight, shown
  // read-only alongside the editable fields.
  const unitSymbol =
    unitOptions.find((option) => option.value === watchedUnit)?.shortLabel ??
    '';
  const itemsPerPackValue = parseWeightField(watchedItemsPerPack);
  const itemWeightValue = parseWeightField(watchedItemWeight);
  const packWeightValue =
    itemsPerPackValue !== undefined && itemWeightValue !== undefined
      ? itemsPerPackValue * itemWeightValue
      : undefined;

  const canSubmit =
    !!target &&
    !isLoading &&
    packWeightComplete &&
    (target === 'other' ||
      (target === 'food' &&
        !!foodModel &&
        (foodModel === 'modelled_food' || !!selectedBasicFood)) ||
      (target === 'packaging' && !!packagingTypeUuid));

  /** The pack-weight override from the form — single-item edits only. */
  const buildPackWeightPayload = () => {
    if (
      !isSingleItem ||
      !target ||
      target === 'other' ||
      itemDetails?.inputMode === 'total-weight'
    )
      return {};
    const values = form.getValues();
    const itemsPerPack = parseWeightField(values.itemsPerPack);
    if (target === 'packaging') return { itemsPerPack };
    return {
      itemsPerPack,
      itemMassOrVolume: parseWeightField(values.itemWeight),
      itemUnitUuid: values.unit || undefined,
    };
  };

  const applyToExistingFoodActivities = async () => {
    const activityUuids = selectedFoodActivityUuids ?? [];
    const packWeight = buildPackWeightPayload();
    try {
      await foodActivityBulk.mutateAsync({
        data:
          foodModel === 'basic_food'
            ? {
                action: 'model-as-basic-food',
                uuids: activityUuids,
                basicFoodUuid: selectedBasicFood?.uuid,
                ...packWeight,
              }
            : {
                action: 'model-as-recipe',
                uuids: activityUuids,
                ...packWeight,
              },
      });
      resolveReviewRows(activityUuids);
      toast({
        title:
          foodModel === 'basic_food'
            ? 'Food match updated'
            : 'Modelling as recipes',
        description: `Applied to ${selectedCount} item${
          selectedCount === 1 ? '' : 's'
        }.`,
      });
      return true;
    } catch (error) {
      toast({
        title: "Couldn't update items",
        description:
          'There was an error updating the selected items. Please try again.',
        variant: 'destructive',
      });
      return false;
    }
  };

  const handleConfirm = async () => {
    if (!target || !canSubmit) return;

    const changeTypeOptions: ChangeItemTypeOptions = {
      ...(target === 'food'
        ? {
            foodModel:
              foodModel === 'basic_food'
                ? ('basic-food' as const)
                : ('recipe' as const),
            basicFoodUuid:
              foodModel === 'basic_food' ? selectedBasicFood?.uuid : undefined,
          }
        : {}),
      ...(target === 'packaging' ? { packagingTypeUuid } : {}),
      ...buildPackWeightPayload(),
    };

    const ok =
      target === 'food' && remodelsExistingFood
        ? await applyToExistingFoodActivities()
        : await changeType(selectedUuids, target, changeTypeOptions);
    if (ok) onCompleted();
  };

  // The two dependent slots are branch-specific, so they appear only once a
  // type is chosen: food shows Modelled by + Matched food, packaging shows
  // Packaging type + Size. Before a type is picked (bulk, uncertain) or for
  // Other goods, only Item type shows.
  const showFoodSlots = target === 'food';
  const showPackagingSlots = target === 'packaging';

  // Review-flag highlights (warning badge + accent) clear live once the user
  // addresses the flagged field. formState is a reactive proxy, so reading
  // dirtyFields here subscribes to pack-weight edits.
  const { dirtyFields } = form.formState;
  const weightEdited = !!(
    dirtyFields.itemsPerPack ||
    dirtyFields.itemWeight ||
    dirtyFields.unit
  );
  const highlightItemType = !!itemDetails?.itemTypeNeedsReview && !target;
  const highlightMatch =
    !!itemDetails?.matchNeedsReview && !matchEdited && target === 'food';
  // A single badge on the Item modelling heading covers both the type and the
  // match dimensions (mirrors how Pack weight shows one badge).
  const highlightModelling = highlightItemType || highlightMatch;
  const highlightWeight =
    !!itemDetails?.weightNeedsReview && !weightEdited && showPackWeight;

  return (
    <Form {...form}>
      <div className="flex flex-col gap-5 pt-2">
        {!isSingleItem && (
          <Text variant="subtle" className="text-gray-600">
            How would you like to model the {selectedCount} selected item
            {selectedCount === 1 ? '' : 's'}?
          </Text>
        )}

        {isSingleItem && (
          <div className="flex flex-col gap-3">
            <Heading variant="subsection">Original data</Heading>
            <div className="grid grid-cols-12 gap-3">
              <InputFormField
                name="externalId"
                label="Your ID"
                disabled
                className="col-span-3"
              />
              <InputFormField
                name="name"
                label="Item name"
                disabled
                className="col-span-6"
              />
              <InputFormField
                name="inputUnit"
                label="Original input unit"
                disabled
                className="col-span-3"
              />
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Heading variant="subsection">Item modelling</Heading>
            {highlightModelling && (
              <NeedsReviewBadge
                message={
                  highlightItemType
                    ? "We couldn't tell what type of item this is. Please pick one."
                    : 'This match may need review to ensure a high standard of emissions modelling.'
                }
              />
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col gap-2">
              <Text variant="subtle" className="font-medium text-gray-800">
                Item type
              </Text>
              <Select
                value={target ?? undefined}
                onValueChange={handleTargetSelect}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map((type) => {
                    const option = TYPE_OPTIONS[type];
                    const Icon = Icons[option.icon];
                    return (
                      <SelectItem
                        key={type}
                        value={type}
                        icon={<Icon className="size-4 text-primary" />}
                        description={option.description}
                      >
                        {option.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {showFoodSlots && (
              <>
                <div className="flex flex-col gap-2">
                  <Text variant="subtle" className="font-medium text-gray-800">
                    Modelled by
                  </Text>
                  <Select
                    value={foodModel ?? undefined}
                    onValueChange={handleFoodModelSelect}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {FOOD_MODEL_ORDER.map((option) => {
                        const meta = FOOD_MODEL_OPTIONS[option];
                        const Icon = Icons[meta.icon];
                        return (
                          <SelectItem
                            key={option}
                            value={option}
                            icon={<Icon className="size-4 text-primary" />}
                            description={meta.description}
                          >
                            {meta.label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2">
                  <Text variant="subtle" className="font-medium text-gray-800">
                    Matched food
                  </Text>
                  <div>
                    <IngredientPicker
                      // Remount on food-model change so the picker's internal
                      // selection resets alongside selectedBasicFood.
                      key={foodModel ?? 'none'}
                      name="basicFoodUuid"
                      include={['base_foods']}
                      onSelect={(item) => {
                        setSelectedBasicFood(item);
                        setMatchEdited(true);
                      }}
                      knownSelectedItem={selectedBasicFood ?? undefined}
                      disabled={foodModel !== 'basic_food'}
                      placeholder={
                        foodModel === 'modelled_food'
                          ? 'Not needed for recipes'
                          : foodModel === 'basic_food'
                            ? 'Select a basic food'
                            : 'Select how to model first'
                      }
                      modal
                    />
                  </div>
                </div>
              </>
            )}

            {showPackagingSlots && (
              <>
                <div className="flex flex-col gap-2">
                  <Text variant="subtle" className="font-medium text-gray-800">
                    Packaging type
                  </Text>
                  <PackagingTypePicker
                    groups={availablePackagingGroups}
                    selectedGroupName={packagingGroupName}
                    onSelectGroup={handlePackagingGroupSelect}
                    disabled={isPackagingLoading}
                    isLoading={isPackagingLoading}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Text variant="subtle" className="font-medium text-gray-800">
                    Size
                  </Text>
                  <Select
                    value={packagingTypeUuid || undefined}
                    onValueChange={setPackagingTypeUuid}
                    disabled={!packagingGroupName}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={
                          packagingGroupName
                            ? 'Select size…'
                            : 'Select a packaging type first'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSizes.map((size) => (
                        <SelectItem key={size.value} value={size.value}>
                          {size.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          {target === 'other' && (
            <Text variant="subtle" className="text-gray-600">
              These items move to Other goods and won&apos;t count towards your
              footprint.
            </Text>
          )}
        </div>

        {showPackWeight && isTotalWeightInput && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Heading variant="subsection">Purchased weight</Heading>
              {highlightWeight && (
                <NeedsReviewBadge message="The weight for this item may need review. Purchases are recorded by weight in the unit below." />
              )}
            </div>
            <Text variant="subtle" className="text-gray-600">
              Quantities for this item were provided by weight, in the unit
              below.
            </Text>
            <div className="grid grid-cols-12 gap-3">
              <SelectFormField
                name="unit"
                label="Unit"
                options={unitOptions}
                className="col-span-4"
                useShortLabel
                disabled
                loadingOptions={isUnitsLoading}
              />
            </div>
          </div>
        )}

        {showPackWeight && !isTotalWeightInput && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Heading variant="subsection">Pack weight</Heading>
              {highlightWeight && (
                <NeedsReviewBadge message="The pack weight for this item may need review to ensure accurate total emissions." />
              )}
            </div>
            <Text variant="subtle" className="text-gray-600">
              For purchases where the unit is a non-standard unit, such as each
              or packs, the purchased weight for this item is calculated using a
              pack weight, which is derived from the fields below.
            </Text>
            <div className="grid grid-cols-12 gap-3">
              <InputFormField
                name="itemsPerPack"
                label="Items per pack"
                placeholder="e.g. 24"
                className={target === 'food' ? 'col-span-3' : 'col-span-4'}
              />
              {target === 'food' && (
                <>
                  <InputFormField
                    name="itemWeight"
                    label="Item weight"
                    placeholder="e.g. 2.5"
                    className="col-span-3"
                  />
                  <SelectFormField
                    name="unit"
                    label="Unit"
                    options={unitOptions}
                    placeholder="Select…"
                    className="col-span-3"
                    useShortLabel
                    loadingOptions={isUnitsLoading}
                  />
                  <div className="col-span-3 flex flex-col gap-2">
                    <Text
                      variant="subtle"
                      className="font-medium text-gray-800"
                    >
                      Pack weight
                    </Text>
                    <div className="flex h-10 items-center rounded-md border bg-gray-50 px-3 text-sm text-gray-500">
                      {packWeightValue !== undefined
                        ? `${packWeightValue.toLocaleString()} ${unitSymbol}`
                        : '—'}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {!isSingleItem && !!target && target !== 'other' && (
          <Text variant="subtle" className="text-gray-600">
            To set pack weights, edit items one at a time.
          </Text>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit}
            loading={isLoading}
            onClick={handleConfirm}
          >
            Apply
          </Button>
        </div>
      </div>
    </Form>
  );
}

export const makeUpdateItemsAction = ({
  currentType,
  resolveUuids,
  resolveFoodActivityUuids,
  reportType,
}: {
  currentType: CurrentItemType;
  /** Map selected rows → inventory-item uuids, for activity-keyed tables. */
  resolveUuids?: BulkAction['resolveUuids'];
  /** Map selected rows → food-activity uuids, for activity-keyed tables. */
  resolveFoodActivityUuids?: BulkAction['resolveFoodActivityUuids'];
  /** Purchased vs sold endpoints; defaults to purchase. */
  reportType?: OperationReportTypeEnum;
}): BulkAction => ({
  kind: 'updateItems',
  label: 'Update items',
  modalTitle: 'Update items',
  icon: 'pencil',
  variant: 'outline',
  // Wide enough for the three-column modelling row and pack-weight grid.
  modalContentClassName: 'max-w-4xl max-h-[90vh]',
  resolveUuids,
  resolveFoodActivityUuids,
  ModalBody: (props) => (
    <UpdateItemsModalBody
      {...props}
      currentType={currentType}
      reportType={reportType}
    />
  ),
});
